@if (productFeed) {
    <div class="product-feed-container d-flex flex-column">
    <div class="view">
        <!-- Tabs -->
        <mat-tab-group #matTabGroup class="product-feed-tabs" (selectedTabChange)="onTabChange($event)" [selectedIndex]="selectedIndex">
            <mat-tab [disabled]="true">
                <ng-template mat-tab-label>
                    <div class="product-feed-title-tab">
                        <span class="category"><small>Product Feed</small></span>
                        <span class="object-name">{{ productFeed.name }}</span>
                    </div>
                </ng-template>
                <!-- No content required -->
            </mat-tab>
            <mat-tab label="Overview">
                <div class="tab-content">
                    @if (isDetailFeed) {
                        <section class="root-ownership" role="status" data-testid="detail-root-ownership">
                            <p class="eyebrow">Root-owned operations</p>
                            <h3>This detail feed only owns detail fetching and transformation.</h3>
                            <p>
                                Enabled state, schedule, notifications, run controls, ingestion history, query testing,
                                and readiness are managed on the root feed.
                            </p>
                            <p data-testid="detail-schedule-ownership">
                                <strong>Schedule:</strong> This detail feed has no independent schedule. Automatic
                                enqueue and manual runs are started from {{ rootFeedName }}.
                            </p>
                            <a [routerLink]="['/ai/product-feeds', detailSourceFeedId]" mat-stroked-button color="primary">
                                Open {{ rootFeedName }}
                            </a>
                        </section>
                    } @else {
                    <header class="readiness-header">
                        <div>
                            <p class="eyebrow">Operator readiness</p>
                            <h3>{{ readinessIssues.length === 0 ? 'Ready for product lookup' : 'Action required' }}</h3>
                            <p>Configuration, publication, and the latest ingestion result for this feed scope.</p>
                        </div>
                        <span class="readiness-badge" [class.readiness-badge--ready]="readinessIssues.length === 0">
                            {{ readinessIssues.length === 0 ? 'Ready' : readinessIssues.length + ' issue(s)' }}
                        </span>
                    </header>

                    @if (readinessIssues.length > 0) {
                        <section class="readiness-issues" role="status">
                            <strong>Resolve before relying on product lookup</strong>
                            <ul>
                                @for (issue of readinessIssues; track issue) {
                                    <li>{{ issue }}</li>
                                }
                            </ul>
                        </section>
                    }

                    <div class="readiness-grid">
                        <section class="readiness-card">
                            <span>Configuration</span>
                            <strong>{{ productFeed.enabled ? 'Enabled' : 'Disabled' }}</strong>
                            <dl>
                                <div><dt>Role</dt><dd>{{ feedGraphRole }}</dd></div>
                                <div><dt>Type</dt><dd>{{ productFeed.type }}</dd></div>
                                <div><dt>Market</dt><dd>{{ productFeed.market || 'Not set' }}</dd></div>
                                @if (productFeed.sourceFeedId) {
                                    <div><dt>XML source</dt><dd>{{ sourceFeedName }}</dd></div>
                                    <div><dt>Allowed hosts</dt><dd>{{ productFeed.detailHostAllowlist?.length || 0 }}</dd></div>
                                }
                            </dl>
                        </section>
                        <section class="readiness-card">
                            <span>Published index</span>
                            <strong>{{ indexStatus }}</strong>
                            <dl>
                                <div><dt>Generation</dt><dd class="monospace-value">{{ productFeed.indexGenerationId || '—' }}</dd></div>
                                <div><dt>Published</dt><dd>{{ productFeed.indexGenerationPublishedAt ? (productFeed.indexGenerationPublishedAt | date:'short') : '—' }}</dd></div>
                                <div><dt>Documents</dt><dd>{{ indexedDocumentCount ?? '—' }}</dd></div>
                                <div><dt>Last fetched</dt><dd>{{ confirmedFetchDate ? (confirmedFetchDate | date:'short') : '—' }}</dd></div>
                            </dl>
                        </section>
                        <section class="readiness-card">
                            <span>Latest ingestion</span>
                            <strong>{{ latestTask ? taskState(latestTask) : 'No run history' }}</strong>
                            @if (latestTask) {
                                <dl>
                                    <div><dt>Origin</dt><dd>{{ taskOrigin(latestTask) }}</dd></div>
                                    <div><dt>Completed</dt><dd>{{ latestTask.completed ? (latestTask.completed | date:'short') : (isLegacyTaskTerminal(latestTask) ? 'Not recorded (legacy)' : '—') }}</dd></div>
                                    <div><dt>Indexed</dt><dd>{{ boundedCount(latestTask.indexedCount) }}</dd></div>
                                    <div><dt>Action</dt><dd>{{ safeTaskAction(latestTask) }}</dd></div>
                                </dl>
                            }
                        </section>
                        <section class="readiness-card" data-testid="schedule-card">
                            <span>Schedule</span>
                            <strong data-testid="schedule-status">{{ scheduleStatus }}</strong>
                            <dl>
                                @if (hasAutomaticSchedule) {
                                    <div data-testid="schedule-mode"><dt>Mode</dt><dd>{{ productFeed.scheduleSettings.schedulingMode }}</dd></div>
                                }
                                @if (showNextScheduledExecution) {
                                    <div data-testid="schedule-next"><dt>Next</dt><dd>{{ productFeed.scheduleSettings.nextExecutionUtc ? (productFeed.scheduleSettings.nextExecutionUtc | date:'short') : '—' }}</dd></div>
                                }
                                <div><dt>Previous</dt><dd>{{ productFeed.scheduleSettings.lastExecutionUtc ? (productFeed.scheduleSettings.lastExecutionUtc | date:'short') : '—' }}</dd></div>
                            </dl>
                            @if (showNonProductionScheduleNotice) {
                                <p class="operation-hint" data-testid="schedule-environment-notice">
                                    Automatic enqueue runs only in production. This environment does not advance the schedule.
                                </p>
                            }
                        </section>
                    </div>

                    @if (hasLuceneSnapshot) {
                        <mat-expansion-panel class="lucene-snapshot-section">
                            <mat-expansion-panel-header>
                                <mat-panel-title>Bounded index sample</mat-panel-title>
                            </mat-expansion-panel-header>
                            <p><strong>Generated:</strong> {{ productFeed.luceneSnapshot?.generatedAt | date:'short' }}</p>
                            <p><strong>Source Type:</strong> {{ productFeed.luceneSnapshot?.sourceType || productFeed.type }}</p>
                            <p><strong>Indexed Documents:</strong> {{ productFeed.luceneSnapshot?.totalDocumentCount }}</p>

                            @if (luceneSnapshotSummary.length > 0) {
                                <ul class="lucene-snapshot-summary">
                                    @for (summary of luceneSnapshotSummary; track summary) {
                                        <li>{{ summary }}</li>
                                    }
                                </ul>
                            }

                            <div class="lucene-snapshot-documents">
                                @for (document of productFeed.luceneSnapshot?.documents ?? []; track $index) {
                                    <section class="lucene-snapshot-document">
                                        <h5>Document {{ $index + 1 }}</h5>
                                        @if (luceneDocumentSummary(document).length > 0) {
                                            <ul class="lucene-snapshot-summary">
                                                @for (summary of luceneDocumentSummary(document); track summary) {
                                                    <li>{{ summary }}</li>
                                                }
                                            </ul>
                                        }
                                        <table class="lucene-snapshot-table">
                                            <tbody>
                                            @for (field of document.fields; track field.name) {
                                                <tr>
                                                    <th>{{ field.name }}</th>
                                                    <td>{{ field.value }}</td>
                                                </tr>
                                            }
                                            </tbody>
                                        </table>
                                    </section>
                                }
                            </div>
                        </mat-expansion-panel>
                    }
                    }
                </div>
            </mat-tab>
            <!-- Settings Tab -->
            <mat-tab label="Settings">
                <div class="tab-content">
                    @if (!isDetailFeed) {
                        <app-client-object-settings [clientSettingsForm]="settingsForm"
                                                    [object]="productFeed"></app-client-object-settings>
                    }
                    <app-product-feed-general-settings
                            [settingsForm]="settingsForm"
                            [productFeedId]="productFeed.id"
                            [availableSourceFeeds]="availableSourceFeeds"></app-product-feed-general-settings>
                    @if (!isDetailFeed) {
                        <app-schedule-settings [scheduleSettingsForm]="scheduleSettingsForm"
                                               [scheduleSettings]="productFeed.scheduleSettings"></app-schedule-settings>
                        <app-notification-settings [notificationForm]="notificationSettingsForm"
                                                   [notificationSettings]="productFeed.notificationSettings"></app-notification-settings>
                    }
                </div>
            </mat-tab>
            @if (!isDetailFeed) {
                <mat-tab label="Filters">
                    <div class="tab-content">
                        <app-filter-criteria
                                [filterForm]="filtersForm"
                                (filterMappingChange)="onFilterChange($event)">
                        </app-filter-criteria>
                    </div>
                </mat-tab>
            }
            <mat-tab label="Field mapping">
                <app-standard-field-mapping
                        [mappingForm]="fieldMappingForm"
                        [defaults]="fieldMappingDefaults">
                </app-standard-field-mapping>
            </mat-tab>
            <mat-tab label="Data Transformation">
                <app-transform-pattern
                        [allowGoogleMerchant]="!isDetailFeed"
                        [transformPatterForm]="transformPatternForm">
                </app-transform-pattern>
            </mat-tab>
            @if (!isDetailFeed) {
                <mat-tab label="Run feed">
                    <div class="tab-content">
                        <h3>Publish a complete feed generation</h3>
                        <p class="operation-warning">
                            This is a real ingestion, not a preview. It crawls the complete root/detail graph and replaces this feed scope only after a successful publication. A safe zero-product result preserves the current index.
                        </p>
                        <button (click)="runFullFeed()"
                                [disabled]="!productFeed.enabled || runningTests || isFormTouched()"
                                color="primary" mat-raised-button>
                            {{ runningTests ? 'Feed run in progress' : 'Run full feed' }}
                        </button>
                        @if (isFormTouched()) {
                            <p class="operation-hint">Save or discard configuration changes before starting a run.</p>
                        } @else if (!productFeed.enabled) {
                            <p class="operation-hint">Enable and save this root feed before starting a new run.</p>
                        }
                        @if (runningTask) {
                            <section class="running-task" aria-live="polite">
                                <strong>{{ taskState(runningTask) }}</strong>
                                <span>Crawled {{ boundedCount(runningTask.crawledCount) }}</span>
                                <span>Transformed {{ boundedCount(runningTask.transformedCount) }}</span>
                                <span>Indexed {{ boundedCount(runningTask.indexedCount) }}</span>
                                <span>Action {{ safeTaskAction(runningTask) }}</span>
                                <span>Reason {{ safeTaskReason(runningTask) }}</span>
                            </section>
                        }
                    </div>
                </mat-tab>
            }
            @if (!isDetailFeed) {
                <mat-tab label="Advanced query">
                    <div class="tab-content">
                        <p class="operation-hint">This runs raw Lucene syntax against this root feed's published index. It does not test language-model planning.</p>
                        <app-product-query-test
                                [embedded]="true"
                                [productFeed]="productFeed">
                        </app-product-query-test>
                    </div>
                </mat-tab>
                <mat-tab label="Product discovery test">
                    <app-product-lookup-diagnostic [productFeed]="productFeed"></app-product-lookup-diagnostic>
                </mat-tab>
                <mat-tab label="Previous runs">
                <div class="tab-content">
                    <h3>Ingestion history</h3>
                    @if (allTasks.length > 0) {
                        <div class="results-table">
                            <table [dataSource]="allTasks" class="mat-elevation-z8" mat-table>
                                <ng-container matColumnDef="state">
                                    <th *matHeaderCellDef mat-header-cell>State</th>
                                    <td *matCellDef="let task" mat-cell>{{ taskState(task) }}</td>
                                </ng-container>
                                <ng-container matColumnDef="origin">
                                    <th *matHeaderCellDef mat-header-cell>Origin</th>
                                    <td *matCellDef="let task" mat-cell>{{ taskOrigin(task) }}</td>
                                </ng-container>
                                <ng-container matColumnDef="created">
                                    <th *matHeaderCellDef mat-header-cell>Queued</th>
                                    <td *matCellDef="let task" mat-cell>{{ task.created | date:'short' }}</td>
                                </ng-container>
                                <ng-container matColumnDef="completed">
                                    <th *matHeaderCellDef mat-header-cell>Completed</th>
                                    <td *matCellDef="let task" mat-cell>{{ task.completed ? (task.completed | date:'short') : (isLegacyTaskTerminal(task) ? 'Not recorded (legacy)' : '—') }}</td>
                                </ng-container>
                                <ng-container matColumnDef="duration">
                                    <th *matHeaderCellDef mat-header-cell>Duration</th>
                                    <td *matCellDef="let task" mat-cell>{{ taskDuration(task) }}</td>
                                </ng-container>
                                <ng-container matColumnDef="counts">
                                    <th *matHeaderCellDef mat-header-cell>Counts</th>
                                    <td *matCellDef="let task" mat-cell>
                                        {{ boundedCount(task.crawledCount) }} / {{ boundedCount(task.transformedCount) }} / {{ boundedCount(task.indexedCount) }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="action">
                                    <th *matHeaderCellDef mat-header-cell>Index action</th>
                                    <td *matCellDef="let task" mat-cell>{{ safeTaskAction(task) }}</td>
                                </ng-container>
                                <ng-container matColumnDef="reason">
                                    <th *matHeaderCellDef mat-header-cell>Reason</th>
                                    <td *matCellDef="let task" mat-cell>{{ safeTaskReason(task) }}</td>
                                </ng-container>
                                <tr *matHeaderRowDef="[ 'state', 'origin', 'created', 'completed', 'duration', 'counts', 'action', 'reason' ]"
                                    mat-header-row></tr>
                                <tr *matRowDef="let row; columns: [ 'state', 'origin', 'created', 'completed', 'duration', 'counts', 'action', 'reason' ];"
                                    mat-row></tr>
                            </table>
                        </div>
                    } @else {
                        <p>No ingestion runs have been recorded for this feed.</p>
                    }
                </div>
                </mat-tab>
            }
        </mat-tab-group>
        <!-- Save and Delete Buttons -->
        @if (showSaveActions) {
            <div class="action-buttons">
                <button (click)="save()" [disabled]="isSaving || (!isFormTouched())" color="primary" mat-raised-button>
                    {{ isSaving ? 'Saving...' : 'Save' }}
                </button>
                <button (click)="delete()" [disabled]="isDeleting" color="warn" mat-raised-button>
                    {{ isDeleting ? 'Deleting...' : 'Delete' }}
                </button>
        </div>
        }
    </div>
    </div>
}
